import React from 'react';
import { VexRhythmStrip } from './VexRhythmStrip';
import { VexPhraseScore } from './VexPhraseScore';
import { buildPhraseScoreBars, phraseScoreCaption } from './phraseScoreBridge';
import { tiesFromScoreEvents } from './vexTieUtils';
import type { SymbolAnalysisHighlight } from '../../types/payloads/symbol_analysis.types';
import type { MusicalToken, TimeSignature } from '../../types/music';
import type { ScoreRenderEvent } from './vexFlowMapper';

export interface SymbolAnalysisStaffProps {
  patternTokens: MusicalToken[];
  timeSignature: TimeSignature;
  targetBarNumber: number;
  modifiers?: {
    ties?: [number, number][];
    keySignature?: string;
    rhythmDots?: number[];
  };
  scoreEvents?: ScoreRenderEvent[];
  scoreEventsByBar?: Record<number, ScoreRenderEvent[]>;
  phraseBarNumbers?: number[];
  barTokenStarts?: number[];
  highlight?: SymbolAnalysisHighlight;
  printMode?: boolean;
  readingMethod?: 'tradicional' | 'takadimi' | 'kodaly';
  showLabels?: boolean;
}

export function SymbolAnalysisStaff({
  patternTokens,
  timeSignature,
  targetBarNumber,
  modifiers,
  scoreEvents,
  scoreEventsByBar,
  phraseBarNumbers,
  barTokenStarts,
  highlight,
  printMode = false,
  readingMethod = 'tradicional',
  showLabels = false,
}: SymbolAnalysisStaffProps) {
  const ties =
    modifiers?.ties?.length
      ? modifiers.ties
      : scoreEvents?.length
        ? tiesFromScoreEvents(scoreEvents)
        : [];

  if (phraseBarNumbers && phraseBarNumbers.length > 1) {
    const bars = buildPhraseScoreBars({
      phraseBarNumbers,
      barTokenStarts,
      patternTokens,
      rhythmDots: modifiers?.rhythmDots ?? [],
      scoreEventsByBar,
      modifiers: { ties: modifiers?.ties },
    });

    return (
      <VexPhraseScore
        bars={bars}
        timeSignature={timeSignature}
        keySignature={modifiers?.keySignature}
        printMode={printMode}
        highlight={highlight}
        caption={phraseScoreCaption(phraseBarNumbers, timeSignature)}
      />
    );
  }

  return (
    <VexRhythmStrip
      pattern={scoreEvents?.length ? scoreEvents.map((e) => e.token) : patternTokens}
      scoreEvents={scoreEvents}
      timeSignature={timeSignature}
      dottedIndices={modifiers?.rhythmDots ?? []}
      ties={ties}
      keySignature={modifiers?.keySignature}
      readingMethod={readingMethod}
      showLabels={showLabels}
      targetBarNumber={targetBarNumber}
      highlight={highlight}
      printMode={printMode}
    />
  );
}
